import { useEffect, useMemo, useState } from "react";

import { Box, Flex, RangeSlider, Text } from "@mantine/core";

const MINUTES_IN_DAY = 24 * 60;
const DEFAULT_STEP_MINUTES = 5;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const normalizeMinutes = (value: number) => {
  const normalized = value % MINUTES_IN_DAY;
  return normalized >= 0 ? normalized : normalized + MINUTES_IN_DAY;
};

const roundToStep = (value: number, stepMinutes: number) =>
  Math.round(value / stepMinutes) * stepMinutes;

const parseTimeValue = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const [hoursRaw, minutesRaw] = value.split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return undefined;
  }

  return hours * 60 + minutes;
};

const formatTimeValue = (value: number) => {
  const normalizedValue = normalizeMinutes(value);
  const hours = Math.floor(normalizedValue / 60)
    .toString()
    .padStart(2, "0");
  const minutes = (normalizedValue % 60).toString().padStart(2, "0");

  return `${hours}:${minutes}`;
};

interface TimeRangeSliderProps {
  disabled?: boolean;
  max: number;
  maxWidth?: number | string;
  min: number;
  stepMinutes?: number;
  valueFrom?: string;
  valueTo?: string;
  onChangeEnd: (valueFrom: string, valueTo: string) => void;
}

const TimeRangeSlider = ({
  disabled = false,
  max,
  maxWidth = "100%",
  min,
  stepMinutes = DEFAULT_STEP_MINUTES,
  valueFrom,
  valueTo,
  onChangeEnd,
}: TimeRangeSliderProps) => {
  const sliderConfig = useMemo(() => {
    const baseMin = min * 60;
    const baseMax = max * 60;

    const parsedFrom = parseTimeValue(valueFrom);
    const parsedTo = parseTimeValue(valueTo);

    const resolvedFrom = clamp(
      roundToStep(parsedFrom ?? baseMin, stepMinutes),
      baseMin,
      baseMax,
    );
    const resolvedToBase = clamp(
      roundToStep(parsedTo ?? baseMax, stepMinutes),
      baseMin,
      baseMax,
    );
    const isOvernightRange =
      parsedFrom !== undefined &&
      parsedTo !== undefined &&
      normalizeMinutes(parsedFrom) > normalizeMinutes(parsedTo);
    const domainMax = isOvernightRange ? baseMax + MINUTES_IN_DAY : baseMax;
    const resolvedTo = isOvernightRange ? resolvedToBase + MINUTES_IN_DAY : resolvedToBase;

    return {
      baseMax,
      baseMin,
      domainMax,
      values: [resolvedFrom, clamp(resolvedTo, resolvedFrom, domainMax)] as [number, number],
    };
  }, [max, min, stepMinutes, valueFrom, valueTo]);

  const [sliderValues, setSliderValues] = useState<[number, number]>(sliderConfig.values);

  useEffect(() => {
    setSliderValues(sliderConfig.values);
  }, [sliderConfig]);

  const formatSliderLabel = (value: number) => formatTimeValue(value);

  const handleSliderChange = (nextValues: [number, number]) => {
    const [rawStart, rawEnd] = nextValues;
    const nextStart = clamp(rawStart, sliderConfig.baseMin, sliderConfig.baseMax);
    const nextEnd = clamp(rawEnd, nextStart, sliderConfig.domainMax);

    setSliderValues([nextStart, nextEnd]);
  };

  const handleSliderChangeEnd = (nextValues: [number, number]) => {
    const [rawStart, rawEnd] = nextValues;
    const nextStart = clamp(rawStart, sliderConfig.baseMin, sliderConfig.baseMax);
    const nextEnd = clamp(rawEnd, nextStart, sliderConfig.domainMax);

    setSliderValues([nextStart, nextEnd]);
    onChangeEnd(formatTimeValue(nextStart), formatTimeValue(nextEnd));
  };

  const sharedTimeBoxStyles = {
    alignItems: "center",
    backgroundColor: disabled ? "var(--mantine-color-gray-0)" : "white",
    border: "1px solid var(--mantine-color-gray-4)",
    borderRadius: "6px",
    color: "var(--mantine-color-gray-9)",
    display: "flex",
    fontSize: "12px",
    fontWeight: 500,
    height: "32px",
    justifyContent: "center",
    minWidth: "62px",
    padding: "0 6px",
  } as const;

  return (
    <Flex align="center" gap="xs" mt={4} style={{ maxWidth, width: "100%" }}>
      <Box style={sharedTimeBoxStyles}>
        <Text size="sm">{formatTimeValue(sliderValues[0])}</Text>
      </Box>

      <Box style={{ flex: 1, padding: "0 2px" }}>
        <RangeSlider
          min={sliderConfig.baseMin}
          max={sliderConfig.domainMax}
          step={stepMinutes}
          minRange={stepMinutes}
          value={sliderValues}
          onChange={handleSliderChange}
          onChangeEnd={handleSliderChangeEnd}
          label={formatSliderLabel}
          disabled={disabled}
          styles={{
            bar: { backgroundColor: "var(--mantine-color-primary-6)" },
            thumb: {
              backgroundColor: "var(--mantine-color-primary-6)",
              borderColor: "white",
              boxShadow: "0 0 0 2px var(--mantine-color-primary-6)",
              height: 14,
              width: 14,
            },
            label: { fontSize: "11px", padding: "3px 6px" },
            track: { backgroundColor: "var(--mantine-color-gray-3)", height: 4 },
          }}
        />
      </Box>

      <Box style={sharedTimeBoxStyles}>
        <Text size="sm">{formatTimeValue(sliderValues[1])}</Text>
      </Box>
    </Flex>
  );
};

export default TimeRangeSlider;
